import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Package, Download, Clock, XCircle, Loader, Shield, ArrowDownToLine } from 'lucide-react';
import { FileIcon } from '../fileIcons';

/* ─── Helpers ─────────────────────────────────────────────── */

function formatSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function formatDate(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('pl-PL', { day: '2-digit', month: 'long', year: 'numeric' });
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ─── Style ───────────────────────────────────────────────── */

const wrapStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem 1rem',
  background: '#fafaf9',
  position: 'relative',
  overflow: 'hidden',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
};

const cardStyle = {
  background: 'rgba(255, 255, 255, 0.55)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
  borderRadius: '20px',
  padding: '30px',
  maxWidth: '560px',
  width: '100%',
  border: '1px solid rgba(255,255,255,0.8)',
  boxShadow: '0 30px 80px rgba(139,92,246,0.15), 0 8px 32px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(139,92,246,0.05)',
  position: 'relative',
  zIndex: 1
};

const gradientText = {
  background: 'linear-gradient(135deg,#0a0a0c 0%,#7c3aed 50%,#0ea5e9 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent'
};

const brandStyle = { ...gradientText, fontSize: '10px', fontWeight: 700, letterSpacing: '0.22em', marginBottom: '14px', textAlign: 'center' };
const titleStyle = { ...gradientText, fontSize: '23px', fontWeight: 600, letterSpacing: '-0.028em', lineHeight: 1.15, textAlign: 'center', marginBottom: '5px', wordBreak: 'break-word' };

const primaryBtn = {
  width: '100%',
  background: 'linear-gradient(135deg,#a78bfa,#38bdf8)',
  color: '#0a0a0c',
  border: 'none',
  borderRadius: '12px',
  padding: '14px',
  fontSize: '13.5px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  letterSpacing: '-0.005em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  boxShadow: '0 6px 24px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.4)',
  transition: 'all 0.15s'
};

const footStyle = {
  textAlign: 'center',
  marginTop: '18px',
  paddingTop: '14px',
  borderTop: '1px solid rgba(139,92,246,0.12)',
  fontSize: '10.5px',
  color: 'rgba(10,10,12,0.55)',
  lineHeight: 1.7
};

const footLink = { color: '#7c3aed', textDecoration: 'none', fontWeight: 500 };

const meshBlobs = [
  { position: 'absolute', top: '-30%', left: '-15%', width: '75%', height: '120%', background: 'radial-gradient(circle, rgba(139,92,246,0.32) 0%, transparent 55%)', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 },
  { position: 'absolute', bottom: '-25%', right: '-10%', width: '65%', height: '90%', background: 'radial-gradient(circle, rgba(56,189,248,0.25) 0%, transparent 55%)', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 },
  { position: 'absolute', top: '15%', right: '10%', width: '30%', height: '40%', background: 'radial-gradient(circle, rgba(244,114,182,0.22) 0%, transparent 60%)', filter: 'blur(60px)', pointerEvents: 'none', zIndex: 0 }
];

/* ─── Elementy wspólne ────────────────────────────────────── */

const MeshBg = () => (
  <>
    {meshBlobs.map((style, i) => <div key={i} style={style} />)}
  </>
);

const Footer = () => (
  <div style={footStyle}>
    <Shield size={11} strokeWidth={1.75} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '3px', color: 'rgba(10,10,12,0.55)' }} />
    Bezpieczne udostępnianie · Szyfrowane hasła
    <br />
    <a href="/privacy" style={footLink}>Polityka Prywatności</a>
    {' · © 2026 MT Capital'}
  </div>
);

const Shell = ({ children }) => (
  <div style={wrapStyle}>
    <MeshBg />
    <div style={cardStyle}>{children}</div>
  </div>
);

/* ─── Strona udostępnienia ────────────────────────────────── */

export default function SharePage() {
  const { id } = useParams();

  const [status, setStatus] = useState('loading');
  const [shareInfo, setShareInfo] = useState(null);
  const [files, setFiles] = useState([]);
  const [token, setToken] = useState('');

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [pwFocus, setPwFocus] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const unlockShare = useCallback(async (pwd) => {
    setUnlocking(true);
    setPasswordError('');
    try {
      const res = await axios.post(`/api/shares/${id}/unlock`, { password: pwd });
      setFiles(res.data.files);
      setShareInfo(info => ({ ...info, ...res.data }));
      setToken(pwd);
      setStatus('unlocked');
    } catch (err) {
      if (err.response?.status === 401) setPasswordError('Nieprawidłowe hasło');
      else if (err.response?.status === 410) setStatus('expired');
      else setPasswordError('Błąd serwera');
    } finally {
      setUnlocking(false);
    }
  }, [id]);

  useEffect(() => {
    axios.get(`/api/shares/${id}`)
      .then(res => {
        setShareInfo(res.data);
        if (res.data.hasPassword) setStatus('locked');
        else unlockShare('');
      })
      .catch(err => {
        setStatus(err.response?.status === 410 ? 'expired' : 'error');
      });
  }, [id, unlockShare]);

  const downloadFile = (filename) => {
    const url = `/api/shares/${id}/files/${encodeURIComponent(filename)}`;
    if (!token) {
      window.location.href = url;
      return;
    }
    fetch(url, { headers: { 'x-share-token': token } })
      .then(r => r.blob())
      .then(blob => triggerBlobDownload(blob, filename))
      .catch(() => alert('Błąd pobierania'));
  };

  const downloadAll = () => {
    const url = `/api/shares/${id}/zip`;
    if (!token) {
      window.location.href = url;
      return;
    }
    fetch(url, { headers: { 'x-share-token': token } })
      .then(r => r.blob())
      .then(blob => triggerBlobDownload(blob, `${shareInfo?.name || 'pliki'}.zip`))
      .catch(() => alert('Błąd pobierania'));
  };

  /* ─── Ładowanie ─── */

  if (status === 'loading') {
    return (
      <>
        <Shell>
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem', color: '#7c3aed' }}>
              <Loader size={32} strokeWidth={1.75} className="spin" />
            </div>
            <p style={{ color: 'rgba(10,10,12,0.6)', fontSize: '13.5px' }}>Ładowanie...</p>
          </div>
        </Shell>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}} .spin{animation:spin 1s linear infinite}`}</style>
      </>
    );
  }

  /* ─── Wygasł / błąd ─── */

  if (status === 'expired' || status === 'error') {
    const isExpired = status === 'expired';
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem', color: isExpired ? '#c2410c' : '#dc2626' }}>
            {isExpired ? <Clock size={48} strokeWidth={1.5} /> : <XCircle size={48} strokeWidth={1.5} />}
          </div>
          <div style={brandStyle}>MT CAPITAL</div>
          <h2 style={{ ...titleStyle, fontSize: '20px', marginBottom: '8px' }}>
            {isExpired ? 'Link wygasł' : 'Nie znaleziono'}
          </h2>
          <p style={{ color: 'rgba(10,10,12,0.6)', fontSize: '13.5px', lineHeight: 1.55 }}>
            {isExpired
              ? 'Ten link nie jest już aktywny. Skontaktuj się z nadawcą, aby otrzymać nowy.'
              : 'Ten link nie istnieje lub został usunięty.'}
          </p>
          <Footer />
        </div>
      </Shell>
    );
  }

  /* ─── Chroniony hasłem ─── */

  if (status === 'locked') {
    return (
      <Shell>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem', color: '#7c3aed' }}>
            <Lock size={38} strokeWidth={1.5} />
          </div>
          <div style={brandStyle}>MT CAPITAL</div>
          <h1 style={titleStyle}>{shareInfo?.name || 'Chronione pliki'}</h1>
          <p style={{ fontSize: '13px', color: 'rgba(10,10,12,0.6)', marginTop: '4px' }}>
            Ten link jest chroniony hasłem. Wprowadź hasło, aby uzyskać dostęp.
          </p>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && unlockShare(password)}
            onFocus={() => setPwFocus(true)}
            onBlur={() => setPwFocus(false)}
            placeholder="Wprowadź hasło..."
            autoFocus
            style={{
              width: '100%',
              background: '#fff',
              border: `1.5px solid ${passwordError ? '#dc2626' : (pwFocus ? '#7c3aed' : 'rgba(139,92,246,0.3)')}`,
              borderRadius: '10px',
              padding: '11px 13px',
              fontSize: '13px',
              color: '#0a0a0c',
              boxSizing: 'border-box',
              outline: 'none',
              fontFamily: 'inherit',
              boxShadow: pwFocus ? '0 0 0 3px rgba(139,92,246,0.18)' : '0 2px 6px rgba(139,92,246,0.08)',
              transition: 'all 0.15s'
            }}
          />
          {passwordError && (
            <div style={{ color: '#dc2626', fontSize: '12.5px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <XCircle size={13} strokeWidth={2} /> {passwordError}
            </div>
          )}
        </div>

        <button
          onClick={() => unlockShare(password)}
          disabled={unlocking}
          style={{ ...primaryBtn, opacity: unlocking ? 0.6 : 1, cursor: unlocking ? 'not-allowed' : 'pointer' }}
        >
          <Lock size={14} strokeWidth={2} />
          {unlocking ? 'Weryfikacja...' : 'Odblokuj dostęp'}
        </button>

        <Footer />
      </Shell>
    );
  }

  /* ─── Odblokowany ─── */

  const totalSize = files.reduce((a, f) => a + f.size, 0);

  return (
    <Shell>
      <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem', color: '#7c3aed' }}>
          <Package size={38} strokeWidth={1.5} />
        </div>
        <div style={brandStyle}>MT CAPITAL</div>
        <h1 style={titleStyle}>{shareInfo?.name}</h1>
        <p style={{ color: 'rgba(10,10,12,0.6)', fontSize: '13px', marginTop: '4px' }}>
          {files.length} {files.length === 1 ? 'plik' : 'pliki'} · {formatSize(totalSize)}
          {shareInfo?.expiresAt ? ` · Dostępne do ${formatDate(shareInfo.expiresAt)}` : ''}
        </p>
        {shareInfo?.message && (
          <div style={{ marginTop: '14px', padding: '12px 16px', background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(15px)', WebkitBackdropFilter: 'blur(15px)', borderRadius: '10px', fontSize: '13px', color: '#0a0a0c', textAlign: 'left', borderLeft: '3px solid #7c3aed' }}>
            {shareInfo.message}
          </div>
        )}
      </div>

      <button onClick={downloadAll} style={{ ...primaryBtn, marginBottom: '1.25rem' }}>
        <ArrowDownToLine size={15} strokeWidth={2} />
        Pobierz wszystkie jako ZIP
      </button>

      <div style={{ fontSize: '10px', fontWeight: 600, color: 'rgba(10,10,12,0.5)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' }}>
        Pliki
      </div>

      <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
        {files.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(15px)', WebkitBackdropFilter: 'blur(15px)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: '10px', marginBottom: '6px' }}>
            <FileIcon name={f.name} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#0a0a0c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
              <div style={{ fontSize: '11px', color: 'rgba(10,10,12,0.45)' }}>{formatSize(f.size)}</div>
            </div>
            <button
              onClick={() => downloadFile(f.name)}
              style={{ padding: '7px 13px', fontSize: '12px', fontWeight: 600, background: 'rgba(139,92,246,0.12)', color: '#7c3aed', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '8px', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'inherit' }}
            >
              <Download size={13} strokeWidth={2} /> Pobierz
            </button>
          </div>
        ))}
      </div>

      <Footer />
    </Shell>
  );
}